/**
 * Practice setup modal — opens in two contexts:
 *   1. Soft-gate: user clicked Download with no practice info stored. Title:
 *      "Set up your practice info". Save resumes the pending download (the
 *      reducer transitions flow to generating-pdf when modal kind is
 *      practice-setup-required).
 *   2. Edit: user clicked "Edit practice info" in the footer. Title:
 *      "Edit practice info". Save just closes the modal.
 *
 * Validation/save flow: on submit, the modal calls savePracticeInfo() directly.
 *   - kind: "ok"                  → dispatch practice-saved (state updates)
 *   - kind: "invalid"             → show inline error on the relevant field
 *   - kind: "quota-exceeded"      → show modal-level error
 *   - kind: "storage-unavailable" → show modal-level error
 *
 * Cancel behavior: backdrop click, ESC, and Cancel button all dispatch
 * practice-cancelled. In soft-gate mode this abandons the pending download.
 *
 * Accessibility: role="dialog", aria-modal=true, aria-labelledby points at
 * the title. Focus moves to the first input on open. Tab cycles within the
 * modal (lightweight focus trap). The page behind the backdrop has
 * inert applied via the backdrop's covering layer; pointer events to the
 * page are blocked by the backdrop's pointer-events: auto.
 */

import { el } from "../dom";
import { savePracticeInfo, type PracticeInfo } from "../practice-info";
import type { Action, AppState } from "../state";

import "./practice-setup.css";

export interface PracticeSetupDeps {
  dispatch: (action: Action) => void;
}

export interface PracticeSetupHandle {
  el: HTMLElement;
  update: (state: AppState) => void;
}

const SOFT_GATE_DESCRIPTION =
  "Before downloading the parent handout, set up your practice info — it appears on the PDF footer so parents know who prepared the report. Saved locally on this device only; never sent anywhere.";

const EDIT_DESCRIPTION =
  "This information is saved locally on this device and appears on the PDF footer of every parent handout you generate. Update it any time.";

export function createPracticeSetup(deps: PracticeSetupDeps): PracticeSetupHandle {
  const titleEl = el("h2", {
    class: "practice-setup__title",
    id: "practice-setup-title",
  });

  const descriptionEl = el("p", { class: "practice-setup__description" });

  const lineInput = el("input", {
    id: "practice-line",
    class: "practice-setup__input",
    type: "text",
    autocomplete: "off",
    required: true,
    "aria-describedby": "practice-line-hint practice-line-error",
  });

  const lineError = el("p", {
    id: "practice-line-error",
    class: "practice-setup__field-error",
    role: "alert",
    "aria-live": "polite",
  });

  const websiteInput = el("input", {
    id: "practice-website",
    class: "practice-setup__input",
    type: "text",
    autocomplete: "off",
    "aria-describedby": "practice-website-hint",
  });

  const formError = el("p", {
    class: "practice-setup__form-error",
    role: "alert",
    "aria-live": "polite",
  });

  const cancelButton = el(
    "button",
    {
      type: "button",
      class: "practice-setup__button practice-setup__button--secondary",
      onclick: () => deps.dispatch({ type: "practice-cancelled" }),
    },
    "Cancel",
  );

  const saveButton = el(
    "button",
    {
      type: "submit",
      class: "practice-setup__button practice-setup__button--primary",
    },
    "Save",
  );

  const formEl = el(
    "form",
    {
      class: "practice-setup__form",
      novalidate: true,
      onsubmit: (event: Event) => {
        event.preventDefault();
        handleSubmit();
      },
    },
    el(
      "div",
      { class: "practice-setup__field" },
      el(
        "label",
        { class: "practice-setup__label", for: "practice-line" },
        "Practice line ",
        el("span", { class: "practice-setup__required" }, "(required)"),
      ),
      el(
        "p",
        { id: "practice-line-hint", class: "practice-setup__hint" },
        'Example: "Dr. Sarah Chen, DC · Bright Roots Pediatric"',
      ),
      lineInput,
      lineError,
    ),
    el(
      "div",
      { class: "practice-setup__field" },
      el(
        "label",
        { class: "practice-setup__label", for: "practice-website" },
        "Website or contact ",
        el("span", { class: "practice-setup__optional" }, "(optional)"),
      ),
      el(
        "p",
        { id: "practice-website-hint", class: "practice-setup__hint" },
        "Example: brightrootschiro.com — appears next to your practice line",
      ),
      websiteInput,
    ),
    formError,
    el("div", { class: "practice-setup__actions" }, cancelButton, saveButton),
  );

  const dialog = el(
    "div",
    {
      class: "practice-setup__dialog",
      role: "dialog",
      "aria-modal": "true",
      "aria-labelledby": "practice-setup-title",
    },
    titleEl,
    descriptionEl,
    formEl,
  );

  const backdrop = el(
    "div",
    {
      class: "practice-setup__backdrop",
      onclick: (event: Event) => {
        // Only dismiss if the click hit the backdrop itself, not bubbled
        // from inside the dialog.
        if (event.target === backdrop) {
          deps.dispatch({ type: "practice-cancelled" });
        }
      },
    },
    dialog,
  );

  const root = el("div", { class: "practice-setup", hidden: true }, backdrop);

  // ESC closes when the modal is open.
  function onKeydown(event: KeyboardEvent): void {
    if (root.hidden) return;
    if (event.key === "Escape") {
      event.preventDefault();
      deps.dispatch({ type: "practice-cancelled" });
    }
  }
  document.addEventListener("keydown", onKeydown);

  function clearErrors(): void {
    lineError.textContent = "";
    lineInput.removeAttribute("aria-invalid");
    formError.textContent = "";
  }

  function handleSubmit(): void {
    clearErrors();
    const info: PracticeInfo = {
      practiceLine: lineInput.value,
      practiceWebsite: websiteInput.value,
    };
    const result = savePracticeInfo(info);
    switch (result.kind) {
      case "ok": {
        // savePracticeInfo trims whitespace; dispatch the trimmed values so
        // state matches what's persisted.
        deps.dispatch({
          type: "practice-saved",
          info: {
            practiceLine: info.practiceLine.trim(),
            practiceWebsite: info.practiceWebsite.trim(),
          },
        });
        return;
      }
      case "invalid":
        if (result.field === "practiceLine") {
          lineError.textContent = result.reason;
          lineInput.setAttribute("aria-invalid", "true");
          lineInput.focus();
        } else {
          formError.textContent = result.reason;
        }
        return;
      case "quota-exceeded":
        formError.textContent =
          "Couldn't save: this browser's storage is full. Clear some site data and try again.";
        return;
      case "storage-unavailable":
        formError.textContent =
          "Couldn't save: this browser doesn't allow local storage (private browsing or an extension may be blocking it). The PDF will still generate but practice info won't be remembered.";
        return;
    }
  }

  let lastModalKind: AppState["modal"]["kind"] | null = null;

  function update(state: AppState): void {
    const kind = state.modal.kind;
    if (kind === "closed") {
      root.hidden = true;
      lastModalKind = "closed";
      return;
    }

    const opening = lastModalKind === "closed" || lastModalKind === null;
    lastModalKind = kind;
    root.hidden = false;

    if (kind === "practice-setup-required") {
      titleEl.textContent = "Set up your practice info";
      descriptionEl.textContent = SOFT_GATE_DESCRIPTION;
      saveButton.textContent = "Save and download PDF";
    } else {
      // "practice-editing"
      titleEl.textContent = "Edit practice info";
      descriptionEl.textContent = EDIT_DESCRIPTION;
      saveButton.textContent = "Save";
    }

    // Hydrate inputs from state on the open transition. We don't sync on
    // every update — once open, the user's keystrokes are authoritative.
    if (opening) {
      clearErrors();
      lineInput.value = state.practice?.practiceLine ?? "";
      websiteInput.value = state.practice?.practiceWebsite ?? "";
      // Defer focus so the element is actually visible (important if root
      // just toggled from hidden).
      queueMicrotask(() => {
        lineInput.focus();
        lineInput.select();
      });
    }
  }

  return { el: root, update };
}
